import { UseFormReturn } from 'react-hook-form';
import { IFieldElem } from '@models/IFormData';
import { ISetValues } from '@models/Forms/IForms';
import { clearTables, addRow } from '@utils/managers/ChangesManager';
import { setValueTyped } from '@utils/helpers';
import { SetConditionManager } from '@utils/managers/SetConditionManager';
import type { InvokeSavedChange } from '@utils/managers/ChangesManager';

export function extractFirstColumnName(display: string): string | undefined {
    const match = display.match(/\[.*?\]/g);
    const raw = match?.[0];
    return raw ? raw.replace('[', '').replace(']', '') : undefined;
}

/**
 * Извлекает значения из массива элементов по атрибуту
 * @param values Массив элементов
 * @param getAttrValue Функция получения значения атрибута из элемента
 * @param attr Имя атрибута
 * @param separator Разделитель для склейки
 */
export function extractValuesFromItems<T>(
    values: T[] | undefined,
    getAttrValue: (item: T, attr: string) => any,
    attr: string,
    separator: string = '|',
): string {
    if (values === undefined) {
        return '';
    }
    const result: string[] = [];
    values.forEach((item) => {
        const elem = getAttrValue(item, attr);
        result.push(elem?.toString() ?? '');
    });
    return result.join(separator);
}

/**
 * Атрибут отсутствует у элемента (не настроен в справочнике/адресной книге).
 * Пустое значение существующего атрибута (`null`) отсутствием не считается —
 * оно должно записаться в поле как пустое.
 */
function hasMissingAttribute<T>(
    values: T[] | undefined,
    getAttrValue: (item: T, attr: string) => unknown,
    attr: string,
): boolean {
    if (values === undefined) {
        return false;
    }
    return values.some((item) => {
        try {
            return getAttrValue(item, attr) === undefined;
        } catch {
            return true;
        }
    });
}

export interface ProcessSetValuesParams {
    setValues: ISetValues | undefined;
    fields: Record<string, IFieldElem>;
    formMethods: UseFormReturn<any>;
    isEdit: boolean;
    isNew: boolean;
    value: any[] | undefined;
    getAttrValue: (item: any, attr: string) => any;
    displayFormat: string | undefined;
    fieldKey: string;
    idField: number;
    hasAddRows?: boolean;
    hasSourceTable?: boolean;
    onSavedSet?: (key: string, value: any) => Promise<void> | void;
    invokeSavedChange?: InvokeSavedChange;
    activeTargets?: Set<string>;
}

/**
 * Обрабатывает setValues: sets, clearTables, addRows
 * Возвращает обновлённый display для последующего parseDisplayFormat
 */
export async function processSetValues(params: ProcessSetValuesParams): Promise<string> {
    const {
        setValues,
        fields,
        formMethods,
        isEdit,
        isNew,
        value,
        getAttrValue,
        displayFormat,
        fieldKey,
        idField,
        hasAddRows = false,
        hasSourceTable = false,
        onSavedSet,
        invokeSavedChange,
        activeTargets = new Set(),
    } = params;

    let display = displayFormat ?? '';

    if (setValues?.sets) {
        for (const item of setValues.sets) {
            const field = fields[item.key];
            if (field === undefined) {
                continue;
            }
            if (hasMissingAttribute(value, getAttrValue, item.attr)) {
                continue;
            }
            const val = extractValuesFromItems(value, getAttrValue, item.attr, item.separator || '|') as any;
            if (display.indexOf(item.key) !== -1) {
                const fieldVal = '{' + item.key + '}';
                const source = new RegExp(fieldVal.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g');
                display = display.replace(source, val);
            }
            const typedVal = setValueTyped(val, item);

            const conditionManager = new SetConditionManager(typedVal, fields, formMethods, isNew, isEdit);
            const conditionResult = await conditionManager.checkConditionAsync(item.setCondition);
            if (conditionResult) {
                formMethods.setValue(field.name as any, typedVal, { shouldDirty: true });
                if (item.invokeChanges) {
                    const savedValue = formMethods.getValues(field.name as any);
                    if (invokeSavedChange) {
                        await invokeSavedChange({ key: item.key }, savedValue, undefined, activeTargets);
                    } else {
                        await onSavedSet?.(item.key, savedValue);
                    }
                }
            }
        }
    }

    clearTables(setValues?.clearTables ?? [], formMethods, fields);
    if (hasAddRows && setValues?.addRows) {
        await addRow(setValues.addRows, formMethods, fields, '', undefined, undefined, invokeSavedChange, activeTargets);
    }

    if (display.indexOf(fieldKey) !== -1 && value !== undefined) {
        const fname = ('fields.[' + idField + '].value') as any;
        const val = formMethods.getValues(fname);
        if (hasSourceTable) {
            const col = extractFirstColumnName(display);
            if (col) {
                display = (val as any[]).map((item: any) => item[col]).join(', ');
            }
        } else {
            const fieldVal = '{' + fieldKey + '}';
            const source = new RegExp(fieldVal.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g');
            display = display.replace(source, val as any);
        }
    }

    return display;
}
